import 'antd/dist/antd.css'
import App from 'next/app'
import type { AppContext, AppProps } from 'next/app'
import { ThemeProvider } from 'styled-components'
import { userAgentFromString } from 'next/server'
import { useRouter } from 'next/router'
import 'yet-another-react-lightbox/styles.css'
import MainLayout from '@/components/v1/layout/main'
import HEIGH_COMPONENT from '@/context/componentContextApp'
import { RootStoreProvider } from '@/store/rootStoreContext'
import { setupRootStore } from '@/store/setupRootStore'
import '../styles/globals.css'
import themes from '../themes'
import { CanonicalTag } from '@/components/v1/canonical-tag/canonical-tag'
import { Providers } from '@/components/v2/molecules/providers/providers'
import { CommonHeaders } from '@/components/v2/atoms/common-headers/common-headers'
import ErrorBoundary from '@/components/v2/atoms/error-boundary/error-boundary'
import { Toaster } from 'sonner'
import type { Viewport } from 'next'
import type { DeviceType } from '@/utils/getIsMobileServer'

export const viewport: Viewport = {
  width: 'device-width',
  maximumScale: 1,
  initialScale: 1,
}

const rootStore = setupRootStore()

type MyAppProps = AppProps & {
  deviceType: DeviceType
}

function MyApp({ Component, pageProps, deviceType }: MyAppProps) {
  // Keep the A/B testing and ad scripts off the legacy admin pages, matching
  // the App Router layout.
  const { pathname } = useRouter()

  return (
    <>
      <CommonHeaders withMarketingScripts={!pathname.startsWith('/admin')} />
      <CanonicalTag />
      <ErrorBoundary>
        <Providers deviceType={deviceType}>
          <RootStoreProvider value={rootStore}>
            <ThemeProvider theme={themes}>
              <MainLayout pageProps={pageProps}>
                <HEIGH_COMPONENT Component={Component} pageProps={pageProps} />
                <Toaster position="top-center" closeButton richColors />
              </MainLayout>
            </ThemeProvider>
          </RootStoreProvider>
        </Providers>
      </ErrorBoundary>
    </>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext)

  const userAgent =
    appContext.ctx.req?.headers['user-agent'] ||
    (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  const { device } = userAgentFromString(userAgent)

  let deviceType: DeviceType = 'desktop'
  if (device.type === 'mobile') deviceType = 'mobile'
  else if (device.type === 'tablet') deviceType = 'tablet'

  return { ...appProps, deviceType }
}

export default MyApp
